import { useState, useEffect, useCallback } from "react"
import ProductPerformanceTable from "./products/ProductPerformanceTable"
import BestSellingProducts from "./products/BestSellingProducts"
import WorstSellingProducts from "./products/WorstSellingProducts"
import GlobalLoader from "@/components/Loading/GlobalLoader"
import styles from "../analytics.module.css"
import { api } from "@/utils/apiService"
import { useAuth } from "@/components/context/AuthContext"
import { useTheme } from "@/components/context/ThemeContext"

export default function ProductAnalytics() {
	const {theme} = useTheme()
	const { token } = useAuth()
	const [data, setData] = useState(null)
	const [bestSellingProducts, setBestSellingProducts] = useState(null)
	const [worstSellingProducts, setWorstSellingProducts] = useState(null)
	const [loading, setLoading] = useState(true)
	const [period, setPeriod] = useState("monthly")
	const [sortBy, setSortBy] = useState("profit")
	const [order, setOrder] = useState("desc")

	const fetchProductData = useCallback(async (period, sortBy, order) => {
		setLoading(true)
		try {
			const res = await api.get("admin/analytics/products/performance", {
				headers: {
					Authorization: `Bearer ${token}`
				},
				params: {
					period,
					sortBy,
					order
				}
			})

			setData(res.data.data)
		} catch (error) {
			console.log("Error Fetching Product Analytics ::", error)
		} finally {
			setLoading(false)
		}
	}, [])

	const fetchBestSellingProduct = useCallback(async (period) => {
		setLoading(true)
		try {
			const res = await api.get("admin/analytics/products/best-selling", {
				headers: {
					Authorization: `Bearer ${token}`
				},
				params: {
					period,
				}
			})

			setBestSellingProducts(res.data.data)
		} catch (error) {
			console.log("Error Fetching Best Selling Product ::", error)
		} finally {
			setLoading(false)
		}
	}, [])

	const fetchWorstSellingProduct = useCallback(async (period) => {
		setLoading(true)
		try {
			const res = await api.get("admin/analytics/products/best-selling", {
				headers: {
					Authorization: `Bearer ${token}`
				},
				params: {
					period,
				}
			})

			setWorstSellingProducts(res.data.data)
		} catch (error) {
			console.log("Error Fetching Best Selling Product ::", error)
		} finally {
			setLoading(false)
		}
	}, [])
	

	useEffect(() => {
		fetchProductData(period, sortBy, order)
		fetchBestSellingProduct(period)
		fetchWorstSellingProduct(period)
	}, [period, sortBy, order])

	useEffect(() => {
		fetchBestSellingProduct(period)
		fetchWorstSellingProduct(period)
	}, [period])

	return (
		<div className={`${styles.analyticsPage} ${theme==="dark"?"dark":""}`}>
			<div className={styles.header}>
				<h1>Product Analytics</h1>
				<div className={styles.filters}>
					<select value={period} onChange={(e) => setPeriod(e.target.value)}>
						<option value="daily">Daily</option>
						<option value="weekly">Weekly</option>
						<option value="monthly">Monthly</option>
					</select>
					<select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
						<option value="profit">Sort by Profit</option>
						<option value="revenue">Sort by Revenue</option>
						<option value="orders">Sort by Orders</option>
					</select>
					<select value={order} onChange={(e) => setOrder(e.target.value)}>
						<option value="desc">Descending</option>
						<option value="asc">Ascending</option>
					</select>
				</div>
			</div>

			{loading ? (
				<GlobalLoader
					isLoading={loading}
					variant="spinner"
					message="Loading Product Analytics"
					fullScreen={false}
				/>
			) : (
				<>
					<div className={styles.summaryCards}>
						<div className={styles.summaryCard}>
							<span className={styles.label}>Total Products</span>
							<span className={styles.value}>{data?.summary?.totalProducts}</span>
						</div>
						<div className={styles.summaryCard}>
							<span className={styles.label}>Total Revenue</span>
							<span className={styles.value}>₹{data?.summary?.totalRevenue?.toLocaleString()}</span>
						</div>
						<div className={styles.summaryCard}>
							<span className={styles.label}>Total Profit</span>
							<span className={styles.value}>₹{data?.summary?.totalProfit?.toLocaleString()}</span>
						</div>
						<div className={styles.summaryCard}>
							<span className={styles.label}>Profit Margin</span>
							<span className={styles.value}>{data?.summary?.profitMargin}%</span>
						</div>
					</div>

					<ProductPerformanceTable products={data?.products} />

					<div className={styles.twoColumn}>
						<BestSellingProducts products={bestSellingProducts?.products} />
						<WorstSellingProducts products={worstSellingProducts?.products} />
					</div>
				</>
			)}
		</div>
	)
}
